import { useEffect, useRef, useState } from 'react';
import { CaseStudyItem } from '@/types/case-study';
import { CaseStudyService } from '@/services/supabase/caseStudies';
import { useAuth } from '@/hooks/domains/auth/useAuth';
import { useQueryClient } from '@tanstack/react-query';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseCaseStudyAutosaveOptions {
  caseStudy: Partial<CaseStudyItem>;
  caseStudyId?: string;
  debounceMs?: number;
  enabled?: boolean; // Allow disabling autosave (e.g., for imported data)
  isManualSaving?: boolean; // Disable autosave when manual save is in progress
  onSaveSuccess?: (savedCaseStudy: CaseStudyItem) => void;
  onSaveError?: (error: Error) => void;
}

/**
 * Custom hook for silent autosave of case studies
 *
 * Features:
 * - Debounced saves (default 2 seconds after last change)
 * - Auto-creates draft on first meaningful change
 * - Updates existing drafts silently
 * - Returns save status for subtle UI indicators
 * - No toast notifications (except errors)
 */
export function useCaseStudyAutosave({
  caseStudy,
  caseStudyId,
  debounceMs = 2000,
  enabled = true,
  isManualSaving = false,
  onSaveSuccess,
  onSaveError,
}: UseCaseStudyAutosaveOptions) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [currentCaseStudyId, setCurrentCaseStudyId] = useState<string | undefined>(caseStudyId);
  const hasCreatedRef = useRef(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedDataRef = useRef<string>('');

  // Initialize lastSavedDataRef with current data if editing existing case study
  // This prevents autosave from triggering on initial load
  useEffect(() => {
    if (caseStudyId && lastSavedDataRef.current === '') {
      lastSavedDataRef.current = serializeCaseStudy(caseStudy);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if case study has meaningful content
  const hasMeaningfulContent = () => {
    return !!(
      caseStudy.title?.trim() ||
      caseStudy.description?.trim() ||
      caseStudy.cover_image ||
      caseStudy.sections?.length
    );
  };

  // Serialize case study for comparison
  const serializeCaseStudy = (item: Partial<CaseStudyItem>) => {
    return JSON.stringify({
      title: item.title || '',
      description: item.description || '',
      cover_image: item.cover_image || '',
      sections: item.sections || [],
      status: item.status || 'draft',
      is_featured: item.is_featured || false,
      seo_title: item.seo_title || '',
      seo_description: item.seo_description || '',
      og_image: item.og_image || '',
      creation_method: item.creation_method || 'manual',
    });
  };

  // Autosave effect
  useEffect(() => {
    // Don't autosave if disabled, no user, no meaningful content, or manual save in progress
    if (!enabled || !user || !hasMeaningfulContent() || isManualSaving) {
      // If manual save is in progress, clear any pending autosave timeout
      if (isManualSaving && saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      return;
    }

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout for debounced save
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        // Serialize current data INSIDE setTimeout to capture freshest state
        // This prevents race condition where user continues typing after serialization
        const currentData = serializeCaseStudy(caseStudy);

        // Don't save if data hasn't changed
        if (currentData === lastSavedDataRef.current) {
          return;
        }

        setSaveStatus('saving');

        let savedCaseStudy: CaseStudyItem;

        if (currentCaseStudyId) {
          // Update existing case study - only send fields that exist in database
          // NOTE: Don't send slug to avoid conflicts - database manages slugs
          savedCaseStudy = await CaseStudyService.updateCaseStudy({
            id: currentCaseStudyId,
            title: caseStudy.title,
            description: caseStudy.description,
            cover_image: caseStudy.cover_image,
            sections: caseStudy.sections,
            status: caseStudy.status || 'draft',
            is_featured: caseStudy.is_featured,
            seo_title: caseStudy.seo_title,
            seo_description: caseStudy.seo_description,
            og_image: caseStudy.og_image,
            order_index: caseStudy.order, // Map order to order_index for database
            creation_method: caseStudy.creation_method,
          });
        } else {
          // Create new case study
          if (hasCreatedRef.current) {
            // Already created, skip this save attempt
            // Update lastSavedDataRef to prevent infinite retry loop
            lastSavedDataRef.current = serializeCaseStudy(caseStudy);
            return;
          }

          // Use user.id as profile ID
          const profileId = user.id;

          savedCaseStudy = await CaseStudyService.createCaseStudy(
            {
              title: caseStudy.title || 'Untitled Case Study',
              description: caseStudy.description || '',
              cover_image: caseStudy.cover_image || '',
              sections: caseStudy.sections || [],
              status: 'draft',
              seo_title: caseStudy.seo_title,
              seo_description: caseStudy.seo_description,
              og_image: caseStudy.og_image,
              slug: caseStudy.slug,
              creation_method: caseStudy.creation_method || 'manual',
            },
            profileId
          );

          hasCreatedRef.current = true;
          setCurrentCaseStudyId(savedCaseStudy.id);
        }

        // Update last saved data
        lastSavedDataRef.current = serializeCaseStudy(savedCaseStudy);

        // Invalidate queries to refresh data
        // Invalidate all unified portfolio queries
        queryClient.invalidateQueries({ queryKey: ['unified-portfolio'] });
        queryClient.invalidateQueries({ queryKey: ['unified-case-studies'] });
        queryClient.invalidateQueries({ queryKey: ['case-studies'] }); // Legacy support

        // Invalidate specific case study query for editor
        if (currentCaseStudyId) {
          queryClient.invalidateQueries({ queryKey: ['case-study', currentCaseStudyId] });
        }

        setSaveStatus('saved');
        onSaveSuccess?.(savedCaseStudy);

        // Reset to idle after 2 seconds
        setTimeout(() => {
          setSaveStatus('idle');
        }, 2000);

      } catch (error) {
        console.error('[useCaseStudyAutosave] Auto-save failed:', error);
        setSaveStatus('error');

        // Update last saved data to current to prevent retry loop
        lastSavedDataRef.current = serializeCaseStudy(caseStudy);

        onSaveError?.(error as Error);

        // Reset to idle after 3 seconds
        setTimeout(() => {
          setSaveStatus('idle');
        }, 3000);
      }
    }, debounceMs);

    // Cleanup timeout on unmount
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [caseStudy, currentCaseStudyId, user, debounceMs, enabled, isManualSaving, onSaveSuccess, onSaveError, queryClient]);

  return {
    saveStatus,
    currentCaseStudyId,
    isSaving: saveStatus === 'saving',
  };
}
